"use client";

import { useEffect, useRef } from 'react';
import { Shield, Clock, Star, Sparkles, Headphones, CheckCircle } from 'lucide-react';

export default function WhyChooseSortKaro() {
  const scrollRef = useRef(null);

  const cards = [
    {
      icon: Shield,
      title: "Enhanced Safety & Assurance",
      features: [
        "Verified Background Checks",
        "Insurance & Guarantees",
        "Trained Professionals"
      ],
      color: "bg-blue-50",
      iconColor: "text-blue-600"
    },
    {
      icon: Clock,
      title: "User-Centric Convenience",
      features: [
        "Flexible Scheduling",
        "Instant Booking",
        "Digital Payments"
      ],
      color: "bg-green-50",
      iconColor: "text-green-600"
    },
    {
      icon: Sparkles,
      title: "Seamless Experience",
      features: [
        "Real-time Tracking",
        "Service Reviews",
        "AI Recommendations"
      ],
      color: "bg-purple-50",
      iconColor: "text-purple-600"
    },
    {
      icon: Star,
      title: "Trusted by Thousands",
      features: [
        "High Customer Ratings",
        "Growing Network",
        "Repeat Customers"
      ],
      color: "bg-yellow-50",
      iconColor: "text-yellow-600"
    },
    {
      icon: Sparkles,
      title: "Cleanliness & Hygiene",
      features: [
        "Sanitized Equipment",
        "Safety Protocols",
        "Quality Products"
      ],
      color: "bg-teal-50",
      iconColor: "text-teal-600"
    },
    {
      icon: Headphones,
      title: "Transparency & Support",
      features: [
        "Upfront Pricing",
        "24/7 Support",
        "Clear Process"
      ],
      color: "bg-orange-50",
      iconColor: "text-orange-600"
    },
  ];

  // Triple the cards for seamless infinite scroll
  const infiniteCards = [...cards, ...cards, ...cards];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const cardWidth = 340; // card width + gap
    const totalWidth = cards.length * cardWidth;

    const scroll = () => {
      scrollPosition += 0.5; // Smooth scroll speed

      if (scrollPosition >= totalWidth) {
        scrollPosition = 0;
      }

      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [cards.length]);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50 overflow-hidden" id="why-choose">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-3">
            Why Choose SortKaro?
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Experience premium home services with verified professionals, transparent pricing, and unmatched convenience
          </p>
        </div>

        {/* Horizontal Scrolling Cards */}
        <div className="relative">
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-hidden scrollbar-hide py-4"
            style={{ scrollBehavior: 'auto' }}
          >
            {infiniteCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  className={`
                    min-w-[320px] max-w-[320px] flex-shrink-0
                    bg-white rounded-2xl p-6 
                    border border-gray-100
                    shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                    hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]
                    hover:-translate-y-1
                    transition-all duration-300 ease-out
                    cursor-pointer
                  `}
                >
                  {/* Icon */}
                  <div className={`${card.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-7 h-7 ${card.iconColor}`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">
                    {card.title}
                  </h3>

                  {/* Features */}
                  <ul className="space-y-3">
                    {card.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-[#00489b] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
